import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import StatusDataLoader from "../../services/StatusDataLoader";
import {
  buildPerspectiveSnapshot,
  PerspectiveInsight,
  PerspectiveSection,
} from "../../utils/Calculators/ConversionPerspectiveCalculator";
import { useSeo } from "../../utils/Hooks/useSeo";
import "./PerspectiveSharePage.css";

type MarketData = {
  btcUsdPrice: number;
  btcMarketCapUsd: number;
};

const parsePositiveNumber = (rawValue: string | null): number | undefined => {
  if (!rawValue) {
    return undefined;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const formatPlainNumber = (value: number) =>
  value.toFixed(10).replace(/\.?0+$/, "");

const getInsightById = (
  sections: PerspectiveSection[],
  insightId: string,
): { section: PerspectiveSection; insight: PerspectiveInsight } | undefined => {
  for (const section of sections) {
    for (const insight of section.insights) {
      if (insight.id === insightId) {
        return { section, insight };
      }
    }
  }

  return undefined;
};

export const PerspectiveSharePage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sharedBtcValue = parsePositiveNumber(searchParams.get("btc"));
  const sharedPrice = parsePositiveNumber(searchParams.get("price"));
  const sharedMarketCap = parsePositiveNumber(searchParams.get("mcap"));
  const [marketData, setMarketData] = useState<MarketData>({
    btcUsdPrice: sharedPrice ?? 0,
    btcMarketCapUsd: sharedMarketCap ?? 0,
  });
  const [loading, setLoading] = useState(!sharedPrice || !sharedMarketCap);

  useEffect(() => {
    const loadMissingMarketData = async () => {
      if (sharedPrice && sharedMarketCap) {
        setLoading(false);
        return;
      }

      const status = await StatusDataLoader.getStatusData();
      const btcUsdPrice = sharedPrice ?? status?.market?.btcPriceUsd ?? 0;
      const btcMarketCapUsd = sharedMarketCap ?? status?.market?.btcMarketCapUsd ?? 0;

      setMarketData({ btcUsdPrice, btcMarketCapUsd });
      setLoading(false);
    };

    loadMissingMarketData();
  }, [sharedMarketCap, sharedPrice]);

  const perspectiveSnapshot = useMemo(() => {
    if (!sharedBtcValue || marketData.btcUsdPrice <= 0) {
      return undefined;
    }

    return buildPerspectiveSnapshot({
      btcValue: sharedBtcValue,
      btcUsdPrice: marketData.btcUsdPrice,
      btcMarketCapUsd: marketData.btcMarketCapUsd,
    });
  }, [marketData.btcMarketCapUsd, marketData.btcUsdPrice, sharedBtcValue]);

  const selectedInsight = useMemo(() => {
    if (!perspectiveSnapshot || !id) {
      return undefined;
    }

    return getInsightById(perspectiveSnapshot.sections, id);
  }, [id, perspectiveSnapshot]);

  useSeo({
    title: selectedInsight?.insight?.title ?? "Shared Bitcoin Perspective",
    description:
      selectedInsight?.insight?.note ??
      "Shared BTC perspective card from In Bitcoin Terms.",
    canonicalPath: id ? `/perspective/${encodeURIComponent(id)}` : "/perspective",
    robots: "noindex,follow",
  });

  const converterLink = sharedBtcValue
    ? `/convert?value=${encodeURIComponent(formatPlainNumber(sharedBtcValue))}&currency=BTC`
    : "/convert";

  return (
    <main className="perspective-share-page">
      <article className="perspective-share-card">
        {loading ? (
          <div className="share-loading">Loading shared perspective...</div>
        ) : !sharedBtcValue ? (
          <div>
            <h1>Shared Perspective</h1>
            <p>This link is missing a valid BTC amount.</p>
          </div>
        ) : !selectedInsight ? (
          <div>
            <h1>Shared Perspective</h1>
            <p>The selected perspective card could not be found.</p>
          </div>
        ) : (
          <div>
            <div className="share-kicker">{selectedInsight.section.title}</div>
            <h1>{selectedInsight.insight.title}</h1>
            <div className="share-value">{selectedInsight.insight.value}</div>
            <p className="share-note">{selectedInsight.insight.note}</p>
            <div className="share-meta">
              Based on {formatPlainNumber(sharedBtcValue)} BTC.
            </div>
          </div>
        )}

        <div className="share-actions">
          <a href={converterLink}>Open Full Converter</a>
          <a href="/">Home</a>
        </div>
      </article>
    </main>
  );
};

